import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import TypewriterText from './TypewriterText';
import NeuralNetwork from './NeuralNetwork';
import RealityDistortionField from '../RealityDistortionField';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* AI Reality Distortion + Neural BG */}
      <div className="absolute inset-0 bg-slate-950">
        <RealityDistortionField className="opacity-50" />
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <NeuralNetwork />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="max-w-2xl"
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8"
          >
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium tracking-wide">AI-Ready for Local Business</span>
          </motion.div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
            AI At Work for
            <span className="block mt-2 h-[1.2em]">
              <TypewriterText />
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl">
            We prepare high-quality training data and integrate AI directly into your workflows — so your small business can compete like the big players.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to={createPageUrl('Contact')}>
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white w-44 h-14 text-base font-medium rounded-2xl shadow-lg shadow-blue-500/25 transition-all hover:shadow-blue-500/40 hover:scale-[1.02]">
                Get Started
              </Button>
            </Link>
            <Link to={createPageUrl('Services')}>
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white w-44 h-14 text-base font-medium rounded-2xl shadow-lg shadow-blue-500/25 transition-all hover:shadow-blue-500/40 hover:scale-[1.02]">
                Explore Services
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
